import { computed, ref } from "vue";
import type { ComputedRef, Ref } from "vue";
import {
    UserPlusIcon,
    CheckBadgeIcon,
    UserGroupIcon,
    CreditCardIcon,
    RocketLaunchIcon,
    ClockIcon,
    CalendarIcon,
    ListBulletIcon,
    SparklesIcon,
    StarIcon,
    GiftIcon,
    ArrowPathIcon,
    CurrencyDollarIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type {
    ITabDef,
    SubscriptionConversion,
    TabKey,
    TeamOrigin,
    TrialOutcome,
} from "../types";

interface UseActivityTabs {
    tabs: ComputedRef<ITabDef[]>;
    activeTab: Ref<TabKey>;
    activeTabDef: ComputedRef<ITabDef | null>;
}

export function useActivityTabs(): UseActivityTabs {
    const { t } = useTranslations();
    const activeTab = ref<TabKey>("new_users");

    const tabs = computed((): ITabDef[] => [
        {
            key: "new_users",
            label: t("vue.kiosk_da_new_registrations"),
            icon: UserPlusIcon,
            bgColor: "bg-blue-500",
            badgeClass: "bg-blue-100 text-blue-700",
        },
        {
            key: "verified_emails",
            label: t("vue.kiosk_da_email_verifications"),
            icon: CheckBadgeIcon,
            bgColor: "bg-green-500",
            badgeClass: "bg-green-100 text-green-700",
        },
        {
            key: "new_teams",
            label: t("vue.kiosk_da_new_teams"),
            icon: UserGroupIcon,
            bgColor: "bg-purple-500",
            badgeClass: "bg-purple-100 text-purple-700",
            showPlan: true,
            showOrigin: true,
        },
        {
            key: "new_free_plans",
            label: t("vue.kiosk_da_new_free_plans"),
            icon: CreditCardIcon,
            bgColor: "bg-orange-500",
            badgeClass: "bg-orange-100 text-orange-700",
        },
        {
            key: "free_plan_restarts",
            label: t("vue.kiosk_da_free_plan_restarts"),
            icon: ArrowPathIcon,
            bgColor: "bg-amber-500",
            badgeClass: "bg-amber-100 text-amber-700",
        },
        {
            key: "new_subscriptions",
            label: t("vue.kiosk_da_new_subscriptions"),
            icon: CurrencyDollarIcon,
            bgColor: "bg-emerald-600",
            badgeClass: "bg-emerald-100 text-emerald-700",
            showPlan: true,
            showConversionType: true,
        },
        {
            key: "team_updates",
            label: t("vue.kiosk_da_team_updates"),
            icon: RocketLaunchIcon,
            bgColor: "bg-indigo-600",
            badgeClass: "bg-indigo-100 text-indigo-700",
            showPlan: true,
        },
        {
            key: "ending_trials",
            label: t("vue.kiosk_da_ending_trials"),
            icon: ClockIcon,
            bgColor: "bg-red-500",
            badgeClass: "bg-red-100 text-red-700",
            showPlan: true,
            showTrialOutcome: true,
        },
        {
            key: "yearly_subscriptions",
            label: t("vue.kiosk_da_yearly_subscriptions"),
            icon: CalendarIcon,
            bgColor: "bg-teal-600",
            badgeClass: "bg-teal-100 text-teal-700",
        },
        {
            key: "activity_logs",
            label: t("vue.kiosk_da_activity_logs"),
            icon: ListBulletIcon,
            bgColor: "bg-slate-500",
            badgeClass: "bg-slate-100 text-slate-700",
        },
    ]);

    const activeTabDef = computed(
        (): ITabDef | null =>
            tabs.value.find((tab) => tab.key === activeTab.value) ?? null,
    );

    return { tabs, activeTab, activeTabDef };
}

export function getPlanBadgeClass(level?: string | null): string {
    switch (level) {
        case "scale":
            return "bg-indigo-100 text-indigo-700";
        case "enterprise":
            return "bg-purple-100 text-purple-700";
        case "free-plan":
            return "bg-orange-100 text-orange-700";
        case "trial":
        case "trial-extended":
            return "bg-blue-100 text-blue-700";
        default:
            return "bg-gray-100 text-gray-700";
    }
}

export function getPlanIcon(level?: string | null): object {
    switch (level) {
        case "scale":
            return SparklesIcon;
        case "enterprise":
            return StarIcon;
        case "free-plan":
            return GiftIcon;
        case "trial":
        case "trial-extended":
            return ClockIcon;
        default:
            return CreditCardIcon;
    }
}

export function getOriginBadgeClass(origin?: TeamOrigin): string {
    return origin === "standalone"
        ? "bg-purple-100 text-purple-700"
        : "bg-gray-100 text-gray-600";
}

export function getTrialOutcomeBadgeClass(outcome?: TrialOutcome): string {
    return outcome === "converted"
        ? "bg-emerald-100 text-emerald-700"
        : "bg-red-100 text-red-700";
}

export function getConversionBadgeClass(
    conversion?: SubscriptionConversion,
): string {
    switch (conversion) {
        case "during_trial":
            return "bg-emerald-100 text-emerald-700";
        case "after_trial":
            return "bg-amber-100 text-amber-700";
        default:
            return "bg-gray-100 text-gray-600";
    }
}
